import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Button, Badge, Avatar } from "@realtyflows/ui";
import { getDealDocuments, getDocSignatures, getSignedPdfUrl, getDeals, resendDocSign, voidDocSign, getContractTemplates, downloadTemplatePdf, getContractFolders, createContractFolder, deleteContractFolder } from "../api";
import ConfirmModal from "../components/ConfirmModal";
import useResponsive from "../hooks/useResponsive";

const statusConfig = {
  signed: { color: t.emerald, bg: `${t.emerald}15`, label: "Signed" },
  partially_signed: { color: t.sky, bg: `${t.sky}15`, label: "Agent Signed" },
  sent: { color: t.sky, bg: `${t.sky}15`, label: "Sent" },
  pending: { color: t.amber, bg: `${t.amber}15`, label: "Pending" },
  awaiting: { color: t.amber, bg: `${t.amber}15`, label: "Awaiting" },
  declined: { color: t.coral, bg: `${t.coral}15`, label: "Declined" },
  voided: { color: t.textMuted, bg: `${t.textMuted}15`, label: "Voided" },
};

const FORM_CATEGORIES = [
  { key: "all", label: "All Forms" },
  { key: "Representation", label: "Representation" },
  { key: "Listing", label: "Listing" },
  { key: "Purchase", label: "Purchase" },
  { key: "Disclosure", label: "Disclosure" },
  { key: "Financing", label: "Financing" },
  { key: "Inspection", label: "Inspection" },
  { key: "Negotiation", label: "Negotiation" },
  { key: "Closing", label: "Closing" },
  { key: "Lease", label: "Lease" },
  { key: "Compliance", label: "Compliance" },
];

export default function Contracts({ defaultView, user, workspace }) {
  const navigate = useNavigate();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;

  const [activeTab, setActiveTab] = useState(defaultView === "templates" ? "forms" : defaultView || "folders");
  const [loading, setLoading] = useState(true);

  // Sent Contracts state
  const [documents, setDocuments] = useState([]);
  const [deals, setDeals] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [signatures, setSignatures] = useState({});
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmVoid, setConfirmVoid] = useState(null);

  // Form Library state
  const [forms, setForms] = useState([]);
  const [formCategory, setFormCategory] = useState("all");

  // Folders state
  const [folders, setFolders] = useState([]);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  const [toast, setToast] = useState(null);

  useEffect(() => {
    async function fetchAll() {
      try {
        const [dealsList, contractForms, foldersList] = await Promise.all([
          getDeals(workspace ? { workspace } : {}),
          getContractTemplates(),
          getContractFolders(workspace || undefined),
        ]);
        setDeals(dealsList);
        setForms(contractForms);
        setFolders(foldersList);

        // Load sent documents
        const allDocs = [];
        await Promise.all(
          dealsList.map(async (deal) => {
            try {
              const docs = await getDealDocuments(deal.id);
              docs.forEach((doc) => allDocs.push({ ...doc, deal }));
            } catch {}
          })
        );
        allDocs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setDocuments(allDocs);
      } catch (err) {
        console.error("Failed to load contracts:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchAll();
  }, [workspace]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  const showToast = (message, variant = "success") => setToast({ message, variant });

  // ── Sent Contracts handlers ──
  const loadSignatures = async (dealId, docId) => {
    if (signatures[docId]) return;
    try {
      const sigs = await getDocSignatures(dealId, docId);
      setSignatures((prev) => ({ ...prev, [docId]: sigs }));
    } catch {}
  };

  const handleExpand = (doc) => {
    if (expandedId === doc.id) { setExpandedId(null); }
    else { setExpandedId(doc.id); loadSignatures(doc.deal.id, doc.id); }
  };

  const handleResend = async (doc) => {
    setActionLoading(doc.id);
    try { await resendDocSign(doc.deal.id, doc.id); showToast("Reminder sent"); }
    catch (err) { showToast(err.message || "Failed to resend", "error"); }
    finally { setActionLoading(null); }
  };

  const handleVoid = async (doc) => {
    setActionLoading(doc.id);
    try {
      await voidDocSign(doc.deal.id, doc.id);
      setDocuments((prev) => prev.map((d) => (d.id === doc.id ? { ...d, status: "voided" } : d)));
      showToast("Document voided");
      setConfirmVoid(null);
    } catch (err) { showToast(err.message || "Failed to void", "error"); }
    finally { setActionLoading(null); }
  };

  // ── Folder handlers ──
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const folder = await createContractFolder({ name: newFolderName.trim() }, workspace);
      setFolders(prev => [folder, ...prev]);
      setNewFolderName("");
      setShowCreateFolder(false);
      showToast("Folder created");
    } catch (err) { showToast(err.message || "Failed to create folder", "error"); }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await deleteContractFolder(folderId);
      setFolders(prev => prev.filter(f => f.id !== folderId));
      showToast("Folder deleted");
    } catch (err) { showToast(err.message || "Failed to delete", "error"); }
  };

  // ── PDF download ──
  const handleDownloadPdf = async (tmpl) => {
    try {
      showToast("Generating PDF...");
      const blob = await downloadTemplatePdf(tmpl.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = (tmpl.name || "contract").replace(/[^a-zA-Z0-9]/g, "_") + ".pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast("PDF downloaded!");
    } catch (err) { showToast(err.message || "Failed to download", "error"); }
  };

  // ── Filters ──
  const filterTabs = useMemo(() => {
    const counts = { all: documents.length };
    documents.forEach(d => { counts[d.status] = (counts[d.status] || 0) + 1; });
    return [
      { key: "all", label: `All (${counts.all})` },
      ...(counts.sent ? [{ key: "sent", label: `Sent (${counts.sent})` }] : []),
      ...(counts.signed ? [{ key: "signed", label: `Signed (${counts.signed})` }] : []),
      ...(counts.voided ? [{ key: "voided", label: `Voided (${counts.voided})` }] : []),
    ];
  }, [documents]);

  const filtered = useMemo(() => {
    let result = documents;
    if (filter !== "all") result = result.filter(d => d.status === filter);
    if (search) result = result.filter(d =>
      (d.template_name || d.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (d.deal?.client_name || "").toLowerCase().includes(search.toLowerCase())
    );
    return result;
  }, [documents, filter, search]);

  const filteredForms = useMemo(() => {
    if (formCategory === "all") return forms;
    return forms.filter(f => f.category === formCategory);
  }, [forms, formCategory]);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: t.textMuted, fontFamily: font.body }}>Loading...</div>;

  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      {/* Page Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>Contracts</div>
        {activeTab === "folders" && (
          <Button variant="primary" size="sm" onClick={() => setShowCreateFolder(true)}>
            + New Folder
          </Button>
        )}
      </div>

      {/* Tab Bar */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: `2px solid ${t.border}` }}>
        {[
          { key: "folders", label: "Folders" },
          { key: "forms", label: "Form Library" },
          { key: "sent", label: "Sent Contracts" },
        ].map((tab) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{
              padding: "10px 24px", fontSize: 14, fontWeight: activeTab === tab.key ? 700 : 400,
              color: activeTab === tab.key ? t.emerald : t.textSecondary,
              background: "none", border: "none", cursor: "pointer",
              borderBottom: `3px solid ${activeTab === tab.key ? t.emerald : "transparent"}`,
              marginBottom: -2, fontFamily: font.display, transition: "all 0.15s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ==================== FOLDERS TAB ==================== */}
      {activeTab === "folders" && (
        <div>
          {/* Create Folder Modal */}
          {showCreateFolder && (
            <div style={{
              background: t.surface, border: `1.5px solid ${t.emerald}30`, borderRadius: 12,
              padding: 20, marginBottom: 16, animation: "scaleIn 0.2s ease",
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 12, fontFamily: font.display }}>
                New Folder
              </div>
              <input
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleCreateFolder(); }}
                placeholder="e.g. John Smith - 123 Main St"
                autoFocus
                style={{
                  width: "100%", padding: "10px 14px", borderRadius: 8,
                  border: `1px solid ${t.border}`, fontSize: 13, fontFamily: font.body,
                  color: t.textPrimary, outline: "none", marginBottom: 12,
                  background: t.surfaceWarm,
                }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant="primary" size="sm" onClick={handleCreateFolder}>Create</Button>
                <Button variant="outline" size="sm" onClick={() => setShowCreateFolder(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Folder List */}
          {folders.length === 0 ? (
            <div style={{
              padding: 60, textAlign: "center", background: t.surface,
              border: `1px solid ${t.border}`, borderRadius: 12,
            }}>
              <div style={{ fontSize: 48, opacity: 0.15, marginBottom: 12 }}>{"\uD83D\uDCC1"}</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: t.textSecondary, fontFamily: font.body, marginBottom: 8 }}>
                No folders yet
              </div>
              <div style={{ fontSize: 13, color: t.textMuted, fontFamily: font.body, marginBottom: 20, lineHeight: 1.6 }}>
                Create a folder for each transaction. Add forms, fill them out, and send for signing.
              </div>
              <Button variant="primary" onClick={() => setShowCreateFolder(true)}>
                + Create First Folder
              </Button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {folders.map(folder => (
                <div key={folder.id} style={{
                  background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10,
                  padding: "16px 20px", display: "flex", alignItems: "center", gap: 16,
                  cursor: "pointer", transition: "all 0.15s",
                }}
                  onClick={() => navigate(`/contracts/folders/${folder.id}`)}
                  onMouseEnter={e => e.currentTarget.style.borderColor = t.emerald}
                  onMouseLeave={e => e.currentTarget.style.borderColor = t.border}
                >
                  {/* Folder icon */}
                  <div style={{
                    width: 42, height: 42, borderRadius: 10,
                    background: `${t.emerald}12`, display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={t.emerald} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                    </svg>
                  </div>

                  {/* Name + info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>
                      {folder.name}
                    </div>
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, marginTop: 2 }}>
                      {folder.form_count || 0} form(s) &middot; Created {new Date(folder.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </div>
                  </div>

                  {/* Status */}
                  <Badge variant={folder.status === "signed" ? "emerald" : folder.status === "sent" ? "sky" : "default"}>
                    {folder.status === "signed" ? "Signed" : folder.status === "sent" ? "Sent" : "Draft"}
                  </Badge>

                  {/* Delete */}
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }} style={{
                    background: "none", border: "none", cursor: "pointer", padding: 4, color: t.textMuted,
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ==================== FORM LIBRARY TAB ==================== */}
      {activeTab === "forms" && (
        <div>
          {/* Category filter */}
          <div style={{ display: "flex", gap: 4, marginBottom: 16, flexWrap: "wrap" }}>
            {FORM_CATEGORIES.map(cat => (
              <button key={cat.key} onClick={() => setFormCategory(cat.key)} style={{
                padding: "6px 14px", fontSize: 11, fontWeight: formCategory === cat.key ? 700 : 500,
                color: formCategory === cat.key ? t.emerald : t.textSecondary,
                background: formCategory === cat.key ? `${t.emerald}12` : "transparent",
                border: `1px solid ${formCategory === cat.key ? `${t.emerald}40` : "transparent"}`,
                borderRadius: 6, cursor: "pointer", fontFamily: font.body, transition: "all 0.15s",
              }}>
                {cat.label}
              </button>
            ))}
          </div>

          {/* Form cards */}
          {filteredForms.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: t.textMuted, fontFamily: font.body }}>
              No forms found in this category
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 12 }}>
              {filteredForms.map(form => (
                <div key={form.id} style={{
                  background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12,
                  padding: 20, display: "flex", flexDirection: "column", gap: 12,
                  transition: "all 0.15s",
                }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`,
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                      </svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>{form.name}</div>
                      <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        {form.category} &middot; {form.state || "Illinois"}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 6 }}>
                    <Button variant="primary" size="sm" style={{ flex: 1 }} onClick={() => navigate(`/contracts/templates/${form.id}`)}>
                      Open Form
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(form)}>
                      PDF
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ==================== SENT CONTRACTS TAB ==================== */}
      {activeTab === "sent" && (
        <>
          {/* Filter toolbar */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 4 }}>
              {filterTabs.map((tab) => (
                <button key={tab.key} onClick={() => setFilter(tab.key)} style={{
                  padding: "6px 14px", fontSize: 11, fontWeight: filter === tab.key ? 700 : 500,
                  color: filter === tab.key ? t.emerald : t.textSecondary,
                  background: filter === tab.key ? `${t.emerald}12` : "transparent",
                  border: `1px solid ${filter === tab.key ? `${t.emerald}40` : "transparent"}`,
                  borderRadius: 6, cursor: "pointer", fontFamily: font.body, transition: "all 0.15s",
                }}>
                  {tab.label}
                </button>
              ))}
            </div>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contracts..."
              style={{
                width: 220, background: t.surfaceWarm, border: `1.5px solid ${t.border}`,
                borderRadius: 8, padding: "7px 12px", color: t.textPrimary,
                fontSize: 12, outline: "none", fontFamily: font.body,
              }}
            />
          </div>

          {/* Document list */}
          {filtered.length === 0 ? (
            <div style={{ padding: 60, textAlign: "center", background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12 }}>
              <div style={{ fontSize: 48, opacity: 0.15, marginBottom: 12 }}>{"\uD83D\uDCC4"}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.textSecondary, fontFamily: font.body, marginBottom: 4 }}>
                {filter !== "all" ? `No ${filter} contracts` : "No sent contracts yet"}
              </div>
              <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body, marginBottom: 16 }}>
                Fill a form and send it for signing to see it here
              </div>
              <Button variant="primary" size="sm" onClick={() => setActiveTab("forms")}>Browse Forms</Button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {filtered.map((doc) => {
                const cfg = statusConfig[doc.status] || statusConfig.pending;
                const expanded = expandedId === doc.id;
                const sigs = signatures[doc.id] || [];

                return (
                  <div key={doc.id} style={{
                    background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10,
                    borderLeft: `3px solid ${cfg.color}`, overflow: "hidden", transition: "all 0.15s",
                  }}>
                    {/* Row */}
                    <div onClick={() => handleExpand(doc)} style={{
                      padding: "14px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: 16,
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = t.surfaceWarm}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <div style={{
                        width: 36, height: 36, borderRadius: 8, background: cfg.bg,
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={cfg.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                          {doc.status === "signed" && <polyline points="9 15 11 17 15 13"/>}
                        </svg>
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {doc.template_name || doc.name}
                        </div>
                        <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, marginTop: 2, display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <Avatar name={doc.deal?.client_name || "?"} size={14}/>
                            {doc.deal?.client_name || "Unknown"}
                          </span>
                          {doc.deal?.property_address && (
                            <><span style={{ color: t.border }}>|</span><span>{doc.deal.property_address}</span></>
                          )}
                        </div>
                      </div>

                      {doc.stage && (
                        <div style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: t.surfaceWarm, color: t.textSecondary, fontFamily: font.mono, fontWeight: 600, flexShrink: 0 }}>
                          {doc.stage}
                        </div>
                      )}

                      <Badge variant={doc.status === "signed" ? "emerald" : doc.status === "declined" ? "coral" : doc.status === "sent" ? "sky" : "default"}>
                        {cfg.label}
                      </Badge>

                      <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.mono, flexShrink: 0, minWidth: 80, textAlign: "right" }}>
                        {new Date(doc.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </div>

                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ flexShrink: 0, transition: "transform 0.2s", transform: expanded ? "rotate(180deg)" : "rotate(0)" }}>
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </div>

                    {/* Expanded Detail */}
                    {expanded && (
                      <div style={{ padding: "0 18px 16px", borderTop: `1px solid ${t.border}` }}>
                        <div style={{ display: "flex", gap: 24, paddingTop: 14 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, marginBottom: 8, fontFamily: font.body }}>Signers</div>
                            {sigs.length === 0 ? (
                              <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>No signers found</div>
                            ) : (
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {sigs.map(sig => (
                                  <div key={sig.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: t.surfaceWarm, borderRadius: 8 }}>
                                    <Avatar name={sig.signer_name || sig.signer_email} size={24}/>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{sig.signer_name || "Unknown"}</div>
                                      <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono }}>{sig.signer_email}</div>
                                    </div>
                                    <div style={{ fontSize: 10, fontWeight: 700, fontFamily: font.body, color: sig.status === "signed" ? t.emerald : t.amber }}>
                                      {sig.status === "signed" ? "Signed" : "Awaiting"}
                                    </div>
                                    {sig.signed_at && (
                                      <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono }}>
                                        {new Date(sig.signed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div style={{ width: 200 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, marginBottom: 8, fontFamily: font.body }}>Details</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontFamily: font.body }}>
                              {doc.trigger_type && <div><span style={{ color: t.textMuted }}>Type:</span> <span style={{ fontWeight: 600 }}>{doc.trigger_type}</span></div>}
                              {doc.signed_at && <div><span style={{ color: t.textMuted }}>Signed:</span> <span style={{ color: t.emerald, fontWeight: 600 }}>{new Date(doc.signed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span></div>}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${t.border}` }}>
                          {doc.esign_envelope_id && doc.status !== "voided" && (
                            <Button variant="outline" size="sm" onClick={() => window.open(getSignedPdfUrl(doc.deal.id, doc.id), "_blank")}>
                              {doc.status === "signed" ? "View Signed Document" : "View Document"}
                            </Button>
                          )}
                          {doc.status === "sent" && (
                            <>
                              <Button variant="outline" size="sm" onClick={() => handleResend(doc)} disabled={actionLoading === doc.id}>
                                {actionLoading === doc.id ? "Sending..." : "Resend Reminder"}
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => setConfirmVoid(doc)} style={{ color: t.coral, borderColor: `${t.coral}40` }}>Void</Button>
                            </>
                          )}
                          {(doc.status === "pending" || doc.status === "awaiting") && doc.esign_envelope_id && (
                            <Button variant="outline" size="sm" onClick={() => setConfirmVoid(doc)} style={{ color: t.coral, borderColor: `${t.coral}40` }}>Void</Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Void Confirm Modal */}
      {confirmVoid && (
        <ConfirmModal
          title="Void Document"
          message={`Void "${confirmVoid.template_name || confirmVoid.name}"? This will cancel the signing request.`}
          onCancel={() => setConfirmVoid(null)}
          onConfirm={() => handleVoid(confirmVoid)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: toast.variant === "error" ? "#fee2e2" : "#dcfce7",
          color: toast.variant === "error" ? "#dc2626" : "#16a34a",
          padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600,
          fontFamily: font.body, boxShadow: "0 4px 16px rgba(0,0,0,0.15)", zIndex: 9999,
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
